import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { McpRealtimeStatus, McpTranscriptEntry } from '../mcp-realtime.service';
import { RealtimeSegmentSelectorService } from './realtime-segment-selector.service';
import { RealtimeSegment, RealtimeSegmentId } from './realtime-segment.types';

@Injectable({ providedIn: 'root' })
export class McpRealtimeRouterService {
    readonly activeSegmentId$ = new BehaviorSubject<RealtimeSegmentId>('gemini');
    readonly status$ = new BehaviorSubject<McpRealtimeStatus>('idle');
    readonly transcript$ = new BehaviorSubject<McpTranscriptEntry[]>([]);
    readonly aiSpeaking$ = new BehaviorSubject<boolean>(false);
    readonly micMuted$ = new BehaviorSubject<boolean>(true);
    /**
     * True while the local browser mic is picking up voice-level sound. Comes
     * from the active segment's optional `userSpeaking$`; segments that don't
     * expose it are treated as always-false so the router still emits a value.
     */
    readonly userSpeaking$ = new BehaviorSubject<boolean>(false);
    /** Live mic input level (0..1) from the active segment, for an on-screen meter. */
    readonly inputLevel$ = new BehaviorSubject<number>(0);
    /** Fires when the active segment asks to fully end the visit (voice "goodbye"). */
    readonly endRequested$ = new Subject<void>();

    private activeSegment: RealtimeSegment;
    private segmentSubs: Subscription[] = [];

    constructor(private readonly selector: RealtimeSegmentSelectorService) {
        this.activeSegment = this.selector.getActiveSegment();
        this.activeSegmentId$.next(this.selector.getConfiguredSegmentId());
        this.bindActiveSegmentStreams(this.activeSegment);
    }

    switchSegment(id: RealtimeSegmentId): void {
        const currentId = this.activeSegmentId$.value;
        if (currentId === id) {
            return;
        }

        try {
            this.activeSegment.stop();
        } catch {}

        this.selector.setConfiguredSegmentId(id);
        const next = this.selector.getActiveSegment();
        this.activeSegment = next;
        this.activeSegmentId$.next(id);
        this.bindActiveSegmentStreams(next);
    }

    /**
     * Switch the active segment WITHOUT changing the persisted global default.
     * Used by the kiosk to scope Grok to the kiosk station only; pass null to
     * drop the override and fall back to the persisted/default segment.
     */
    useRuntimeSegment(id: RealtimeSegmentId | null): void {
        this.selector.setRuntimeOverride(id);
        const nextId = this.selector.getConfiguredSegmentId();
        if (nextId === this.activeSegmentId$.value) {
            return;
        }
        try {
            this.activeSegment.stop();
        } catch {}
        const next = this.selector.getActiveSegment();
        this.activeSegment = next;
        this.activeSegmentId$.next(nextId);
        this.bindActiveSegmentStreams(next);
    }

    getActiveSegmentId(): RealtimeSegmentId {
        return this.activeSegmentId$.value;
    }

    private bindActiveSegmentStreams(segment: RealtimeSegment): void {
        this.segmentSubs.forEach((s) => s.unsubscribe());
        this.segmentSubs = [];
        this.segmentSubs.push(segment.status$.subscribe((v) => this.status$.next(v)));
        this.segmentSubs.push(segment.transcript$.subscribe((v) => this.transcript$.next(v)));
        this.segmentSubs.push(segment.aiSpeaking$.subscribe((v) => this.aiSpeaking$.next(v)));
        this.segmentSubs.push(segment.micMuted$.subscribe((v) => this.micMuted$.next(v)));
        // Reset the "user is talking" pulse when switching segments — the
        // previous segment's state is stale the moment we swap. If the new
        // segment doesn't expose the signal, it stays false forever, which is
        // the correct default for engines that don't compute it yet.
        this.userSpeaking$.next(false);
        if (segment.userSpeaking$) {
            this.segmentSubs.push(segment.userSpeaking$.subscribe((v) => this.userSpeaking$.next(v)));
        }
        this.inputLevel$.next(0);
        if (segment.inputLevel$) {
            this.segmentSubs.push(segment.inputLevel$.subscribe((v) => this.inputLevel$.next(v)));
        }
        if (segment.endRequested$) {
            this.segmentSubs.push(segment.endRequested$.subscribe(() => this.endRequested$.next()));
        }
    }

    prepareKioskSession(requireWakeWord: boolean): void {
        const seg = this.activeSegment as RealtimeSegment & {
            prepareKioskSession?(v: boolean): void;
        };
        seg.prepareKioskSession?.(requireWakeWord);
    }

    start(): Promise<void> {
        return this.activeSegment.start();
    }

    prewarm(): Promise<void> {
        // OpenAI is dormant; Gemini auto-starts to stay always ready.
        if (this.activeSegmentId$.value === 'openai') {
            return Promise.resolve();
        }
        return this.activeSegment.prewarm();
    }

    stop(): void {
        this.activeSegment.stop();
    }

    /**
     * Hard reset: tear the active segment all the way down and start it back
     * up. Used by the kiosk "Reset" button when the agent stalls mid-visit and
     * a lighter fix (silence nudge, watchdog) won't recover it.
     */
    async reset(): Promise<void> {
        try {
            this.activeSegment.stop();
        } catch {}
        // Small yield so the socket close event has time to settle before we
        // spin up a fresh session on the same segment.
        await new Promise((resolve) => setTimeout(resolve, 250));
        await this.activeSegment.start();
    }

    setMicMuted(muted: boolean): void {
        this.activeSegment.setMicMuted(muted);
    }

    toggleMic(): void {
        this.activeSegment.toggleMic();
    }

    sendText(text: string): void {
        this.activeSegment.sendText(text);
    }
}
